import { Node, mergeAttributes } from '@tiptap/core';

export interface InlineImageOptions {
    HTMLAttributes: Record<string, any>;
}

/**
 * Minimal inline image node so plain hosted `<img src="https://…">` from rendered email content (the
 * "Original email" section of an email-derived task — see EmailTaskNotes.SanitizeEmailHtml on the backend)
 * actually display in the notes editor. The app does NOT install `@tiptap/extension-image` and StarterKit
 * omits Image, so without this node the email's `<img>` tags are silently stripped on parse. Kept INLINE so
 * images embedded in body/signature paragraphs render in place; this is distinct from `ReferenceToken`
 * (the inserted-attachment token node).
 *
 * Only http(s) sources are accepted on parse — `data:` / `cid:` / `blob:` are dropped (cid: inline images
 * are already swapped for a "[inline image]" placeholder upstream), so the editor never holds inline binary
 * blobs in its document.
 */
export const InlineImage = Node.create<InlineImageOptions>({
    name: 'inlineImage',
    inline: true,
    group: 'inline',
    draggable: true,

    addOptions() {
        return { HTMLAttributes: { class: 'vf-tiptap-inline-image' } };
    },

    addAttributes() {
        return {
            src: { default: null },
            alt: { default: null },
            title: { default: null },
            width: { default: null },
            height: { default: null }
        };
    },

    parseHTML() {
        return [
            {
                tag: 'img[src]',
                getAttrs: (node) => {
                    const src = (node as HTMLElement).getAttribute('src') || '';
                    // null = accept (src/alt/… auto-parsed from the element); false = reject the rule so
                    // non-hosted images (data:/cid:/blob:/relative) are dropped rather than rendered.
                    return /^https?:\/\//i.test(src) ? null : false;
                }
            }
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
    }
});
